import {
	CalendarDays,
	type LucideIcon,
	TrendingDown,
	TrendingUp,
	TriangleAlert,
} from "lucide-react";
import type { ReactNode } from "react";
import type { ConstructionProgressResponse } from "@/adapters/api";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import {
	formatCount,
	formatDays,
	formatPercent,
	formatPointsValue,
	formatShortDate,
} from "../../utils/format.ts";
import type { Milestone } from "../../utils/milestone.ts";

export type KpiTone = "neutral" | "positive" | "negative" | "warning";

export interface KpiCardProps {
	icon: LucideIcon;
	label: string;
	value: string;
	unit?: string;
	sub: string;
	tone?: KpiTone;
}

export function KpiCard({
	icon: Icon,
	label,
	value,
	unit,
	sub,
	tone = "neutral",
}: KpiCardProps) {
	return (
		<div className="kpi-card" data-tone={tone}>
			<div className="kpi-card__header">
				<Icon className="kpi-card__icon" />
				<p className="kpi-card__label">{label}</p>
			</div>
			<p className="kpi-card__value">
				{value}
				{unit && <span className="kpi-card__unit">{unit}</span>}
			</p>
			<p className="kpi-card__sub">{sub}</p>
		</div>
	);
}

export interface KpiRowProps {
	data?: ConstructionProgressResponse;
	isPending: boolean;
	isError: boolean;
	milestone?: Milestone | null;
	isMilestonePending?: boolean;
	trailing?: ReactNode;
}

const KPI_SKELETONS = ["avanco", "desvio", "atrasadas", "marco"];

export function KpiRow({
	data,
	isPending,
	isError,
	milestone,
	isMilestonePending,
	trailing,
}: KpiRowProps) {
	if (isPending) {
		return (
			<div className="kpi-row">
				{KPI_SKELETONS.map((key) => (
					<Skeleton key={key} className="kpi-card-skeleton" />
				))}
				{trailing}
			</div>
		);
	}

	if (isError || !data) {
		return (
			<div className="kpi-row">
				<p className="kpi-row__error dashboard-panel-error" role="status">
					Não foi possível carregar os indicadores da obra.
				</p>
				{trailing}
			</div>
		);
	}

	const behind = data.deviation_points < 0;
	const milestoneValue = isMilestonePending
		? "—"
		: formatDays(milestone?.days ?? null);
	const milestoneSub = milestone
		? `${milestone.name} · ${formatShortDate(milestone.plannedEndDate)}`
		: "Nenhum fim planejado à frente";

	return (
		<div className="kpi-row">
			<KpiCard
				icon={TrendingUp}
				label="Avanço realizado"
				value={formatPercent(data.actual_percent)}
				sub={`Planejado: ${formatPercent(data.planned_percent)}`}
			/>
			<KpiCard
				icon={behind ? TrendingDown : TrendingUp}
				label="Desvio do planejado"
				value={formatPointsValue(data.deviation_points)}
				unit="p.p."
				sub={behind ? "Abaixo do planejado" : "No prazo ou adiantada"}
				tone={behind ? "negative" : "positive"}
			/>
			<KpiCard
				icon={TriangleAlert}
				label="Etapas atrasadas"
				value={formatCount(data.delayed_count)}
				sub={
					data.delayed_count === 0
						? "Nenhuma etapa atrasada"
						: "Abaixo do previsto para hoje"
				}
				tone={data.delayed_count > 0 ? "warning" : "neutral"}
			/>
			<KpiCard
				icon={CalendarDays}
				label="Dias para o próximo fim"
				value={milestoneValue}
				sub={milestoneSub}
			/>
			{trailing}
		</div>
	);
}
